import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDragPlaceholder, CdkDropList } from '@angular/cdk/drag-drop';
import { IBlockForSelect } from '../../../../utils/blocks-helper';

export const LOCKED_BLOCK_NAMES = new Set(['subject', 'preheader']);
const LOCKED_BLOCK_LABELS: Record<string, string> = { subject: 'Subject', preheader: 'Preheader' };

/**
 * Region 2 of the mockup: persistent left panel listing every block in order,
 * with drag-and-drop reordering and a collapse toggle. Reorder is emitted as
 * the same CdkDragDrop event shape the existing base editor's drop() expects
 * (index-based - see base-template-editor.component.ts:585), so the orchestrator
 * forwards it straight through, unchanged.
 */
@Component({
  selector: 'app-sidebar-v2',
  imports: [CommonModule, FormsModule, CdkDropList, CdkDrag, CdkDragHandle, CdkDragPlaceholder],
  templateUrl: './sidebar-v2.component.html',
  styleUrl: './sidebar-v2.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarV2Component {
  @Input() blocks: any[] = [];
  @Input() blockCatalog: IBlockForSelect[] = [];
  @Input() selectedBlockId: string | null = null;
  @Input() collapsed = false;
  @Input() dragDisabled = false;
  /** Mirrors preview-v2's own showAddBlock (enableBlockToolbar && !isTranslator) -
   *  translators and channels without the add-block toolbar never get this button. */
  @Input() showAddBlock = true;
  @Input() addBlockDisabled = false;

  @Output() selectBlock = new EventEmitter<string>();
  @Output() reorder = new EventEmitter<CdkDragDrop<string[]>>();
  @Output() toggleCollapsed = new EventEmitter<void>();
  @Output() deleteBlock = new EventEmitter<string>();
  @Output() duplicateBlock = new EventEmitter<string>();
  /** Mockup's "+ Add new block" button at the bottom of the panel - always
   *  inserts at the very end of the block list (see template-editor-v2's
   *  openSidebarAddBlockV2()), unlike the canvas's per-gap "+" buttons. */
  @Output() addBlockAtEnd = new EventEmitter<void>();

  protected search = '';

  protected onAddBlockAtEndClick(): void {
    if (this.addBlockDisabled) {
      return;
    }
    this.addBlockAtEnd.emit();
  }

  isLocked(block: any): boolean {
    return LOCKED_BLOCK_NAMES.has(block?.name);
  }

  displayName(block: any): string {
    if (LOCKED_BLOCK_LABELS[block?.name]) {
      return LOCKED_BLOCK_LABELS[block.name];
    }
    const catalogEntry = this.blockCatalog.find((c) => c.name === block?.name);
    return catalogEntry?.dropdownName || this.labelize(block?.name);
  }

  private labelize(name: string): string {
    if (!name) {
      return '';
    }
    return name
      .split('-')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  get filteredBlocks(): any[] {
    if (!this.search.trim()) {
      return this.blocks;
    }
    const term = this.search.trim().toLowerCase();
    return this.blocks.filter((b) => this.displayName(b).toLowerCase().includes(term));
  }

  /** Keeps each row's DOM node (and its CDK drag registration) tied to the
   *  same block across re-renders, instead of the default reference-based
   *  diffing - safer while cdkDropList is actively re-parenting these same
   *  nodes during a drag. */
  trackByBlockId(_index: number, block: any): string {
    return block?.blockId;
  }
}
